"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import { PANEL_URL } from "@/lib/panelPrices";
import type { NavItem } from "@/lib/siteContent";
import type { PageTexts } from "@/lib/siteTexts";


const fallbackNavLinks = [
  { id: '1', label: "Главная", href: "/homepage", display_order: 1, is_visible: true, icon: null, target: '_self' },
  { id: '2', label: "Виртуальные машины", href: "/virtual-machines", display_order: 2, is_visible: true, icon: null, target: '_self' },
  { id: '3', label: "Хранилище", href: "/storage", display_order: 3, is_visible: true, icon: null, target: '_self' },
  { id: '4', label: "Тарифы", href: "/pricing", display_order: 4, is_visible: true, icon: null, target: '_self' },
  { id: '5', label: "Документация", href: "/documentation", display_order: 5, is_visible: true, icon: null, target: '_self' },
  { id: '6', label: "Статус", href: "/service-status", display_order: 6, is_visible: true, icon: null, target: '_self' },
];

export default function HeaderClient({ items, texts: t }: { items: NavItem[] | null; texts: PageTexts<"common"> }) {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navLinks = items && items.length > 0 ? items : fallbackNavLinks;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(5,11,24,0.95)] backdrop-blur-xl border-b border-[rgba(79,124,255,0.15)] shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <div className="relative">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00D4FF] to-[#4F7CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,212,255,0.4)]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C8.5 2 5.5 4.5 5 8C3 8.5 1 10.5 1 13C1 16 3.5 18 6.5 18H18C20.5 18 22 16.5 22 14C22 11.5 20 9.5 17.5 9.5C17 6 14.5 2 12 2Z" fill="white" opacity="0.9"/>
                <path d="M9 22L12 18L15 22" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-[#00D4FF] to-[#4F7CFF] blur-lg opacity-40 group-hover:opacity-70 transition-opacity" />
          </div>
          <div>
            <span className="font-display font-bold text-lg text-white tracking-tight">
              Oblako
            </span>
            <span className="text-[#00D4FF] font-bold text-lg">.kg</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            const isStatus = link?.href === "/service-status";
            return (
              <Link
                key={link?.id}
                href={link?.href}
                target={link?.target || '_self'}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  isActive
                    ? "text-[#00D4FF] bg-[rgba(0,212,255,0.08)]"
                    : "text-[#8BA3CC] hover:text-[#E8F0FF] hover:bg-[rgba(79,124,255,0.06)]"
                }`}
              >
                {isStatus && (
                  <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse inline-block" />
                )}
                {link?.label}
                {isActive && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-[#00D4FF]" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a href={`${PANEL_URL}/login`} className="px-4 py-2 text-sm font-medium text-[#8BA3CC] hover:text-[#E8F0FF] transition-colors">
            {t["header.login"]}
          </a>
          <Link href={t["header.cta.href"]} className="btn-primary text-sm font-bold px-5 py-2.5 rounded-lg">
            {t["header.cta.label"]}
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 rounded-lg text-[#8BA3CC] hover:text-white hover:bg-[rgba(79,124,255,0.1)] transition-all"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <Icon name={mobileOpen ? "XMarkIcon" : "Bars3Icon"} size={24} />
        </button>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[rgba(5,11,24,0.98)] backdrop-blur-xl border-b border-[rgba(79,124,255,0.15)] px-6 py-4">
          <nav className="flex flex-col gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.id}
                  href={link?.href}
                  target={link?.target || '_self'}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    isActive
                      ? "text-[#00D4FF] bg-[rgba(0,212,255,0.08)]"
                      : "text-[#8BA3CC] hover:text-white hover:bg-[rgba(79,124,255,0.06)]"
                  }`}
                >
                  {link?.href === "/service-status" && (
                    <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
                  )}
                  {link?.label}
                </Link>
              );
            })}
            <div className="pt-3 mt-2 border-t border-[rgba(79,124,255,0.15)] flex gap-3">
              <a href={`${PANEL_URL}/login`} className="flex-1 py-2.5 text-sm font-medium text-[#8BA3CC] border border-[rgba(79,124,255,0.2)] rounded-lg hover:text-white transition-colors text-center">
                {t["header.mobile.login"]}
              </a>
              <Link href={t["header.cta.href"]} className="flex-1 py-2.5 btn-primary text-sm font-bold rounded-lg text-center">
                {t["header.mobile.cta"]}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}